import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Script } from "./ScriptCard";
import ScriptToGameAdapter from "./ScriptToGameAdapter";

interface ScriptCarouselProps {
  scripts: Script[];
}

const ScriptCarousel = ({ scripts }: ScriptCarouselProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth / 2;
      const newScrollLeft = direction === 'left' ? container.scrollLeft - scrollAmount : container.scrollLeft + scrollAmount;
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Initial check for arrows on mount
  useEffect(() => {
    handleScroll();
  }, [scripts]);
  
  if (scripts.length === 0) {
    return (
      <div className="text-center py-16 flex-grow">
        <h3 className="text-xl font-medium mb-2">No games found</h3>
        <p className="text-muted-foreground">Try adjusting your search criteria</p>
      </div>
    );
  }

  return (
    <div className="relative flex-grow mb-8">
      <button 
        onClick={() => scroll('left')} 
        className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-20 bg-black/70 hover:bg-black/90 text-white rounded-full p-3 transition-all ${!showLeftArrow ? 'opacity-0' : 'opacity-100'} ${!showLeftArrow ? 'pointer-events-none' : ''}`} 
        aria-label="Scroll left"
      >
        <ChevronLeft size={28} />
      </button>

      <div 
        ref={scrollContainerRef} 
        onScroll={handleScroll} 
        className="flex overflow-x-auto scrollbar-hide gap-6 pb-6 snap-x snap-mandatory h-full" 
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <style>
          {`
            .scrollbar-hide::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>

        {scripts.map((script, index) => (
          <ScriptToGameAdapter key={script.id} script={script} index={index} />
        ))}
      </div>

      <button 
        onClick={() => scroll('right')} 
        className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-20 bg-black/70 hover:bg-black/90 text-white rounded-full p-3 transition-all ${!showRightArrow ? 'opacity-0' : 'opacity-100'} ${!showRightArrow ? 'pointer-events-none' : ''}`} 
        aria-label="Scroll right"
      >
        <ChevronRight size={28} />
      </button>
    </div>
  );
};

export default ScriptCarousel;
